import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useRouteMatch} from 'react-router-dom'

import {Box, Grid} from '@mui/material'

import AppointmentList from '@progyny/pages/Consultations/Menopause/AppointmentList'
import CancelConsultation from '@progyny/pages/Consultations/components/CancelConsultation'
import ConsultationHubNavigationRow from '@progyny/pages/Consultations/components/ConsultationHubNavigationRow'
import ConsultationTypeFilterDropdown, {
  type ConsultationTypeFilter,
} from '@progyny/pages/Consultations/components/ConsultationTypeFilterDropdown'
import OnboardingSection from '@progyny/pages/Consultations/components/OnboardingSection'
import {useP3ConsultationAppointments} from '@progyny/pages/Consultations/hooks/useP3ConsultationAppointments'

export type P3ActiveTab = 'overview' | 'upcoming' | 'past'

export interface P3ConsultationViewContentProps {
  basePath: string
  consultation: any
  colors: any
  theme: any
  activeTab: P3ActiveTab
  hub: {
    onboardingConsultationSlug: string
    programSlugs: readonly string[]
  }
  overview: React.ReactNode
  onRefetchConsultationDetail?: (variables?: unknown) => Promise<unknown>
}

const P3ConsultationViewContent: React.FC<P3ConsultationViewContentProps> = ({
  basePath,
  consultation,
  colors,
  theme,
  activeTab,
  hub,
  overview,
  onRefetchConsultationDetail,
}) => {
  const {t} = useTranslation()
  const history = useHistory()
  const {path} = useRouteMatch()
  const [consultationTypeFilter, setConsultationTypeFilter] =
    React.useState<ConsultationTypeFilter>('all')

  const {
    upcoming,
    past,
    hasUpcoming,
    hasPast,
    loading: appointmentsLoading,
    refetchAppointments,
  } = useP3ConsultationAppointments(hub)

  const showTabs = hasUpcoming || hasPast

  const [activeCancelUrl, setActiveCancelUrl] = React.useState<string | null>(
    null
  )

  const navigationItems = [
    {
      itemID: 'overview',
      label: t('consultations_program_session_plan_consultation_overview'),
    },
    ...(hasUpcoming
      ? [
          {
            itemID: 'upcoming',
            label: t(
              'consultations_program_session_plan_upcoming_consultations'
            ),
          },
        ]
      : []),
    ...(hasPast
      ? [
          {
            itemID: 'past',
            label: t('consultations_program_session_plan_past_consultations'),
          },
        ]
      : []),
  ]

  const handleNavItemClick = (itemID: string) => {
    if (itemID === 'overview') {
      history.push(basePath)
    } else if (itemID === 'upcoming') {
      history.push(`${basePath}/upcoming`)
    } else if (itemID === 'past') {
      history.push(`${basePath}/past`)
    }
  }

  const activeNavItem = path.endsWith('/past')
    ? 'past'
    : path.endsWith('/upcoming')
      ? 'upcoming'
      : 'overview'

  const filteredUpcoming = React.useMemo(() => {
    if (consultationTypeFilter === 'all') return upcoming
    if (consultationTypeFilter === 'emotional_program') {
      return upcoming.filter((item) => item.type === 'emotional_program')
    }
    return upcoming.filter((item) => item.type === 'onboarding')
  }, [upcoming, consultationTypeFilter])

  const filteredPast = React.useMemo(() => {
    if (consultationTypeFilter === 'all') return past
    if (consultationTypeFilter === 'emotional_program') {
      return past.filter((item) => item.type === 'emotional_program')
    }
    return past.filter((item) => item.type === 'onboarding')
  }, [past, consultationTypeFilter])

  const handleCancelBack = React.useCallback(() => {
    setActiveCancelUrl(null)
    refetchAppointments()
    if (onRefetchConsultationDetail) {
      onRefetchConsultationDetail()
    }
  }, [refetchAppointments, onRefetchConsultationDetail])

  React.useEffect(() => {
    if (activeTab !== 'overview' && activeCancelUrl) {
      setActiveCancelUrl(null)
    }
  }, [activeTab, activeCancelUrl])

  return (
    <>
      {showTabs && (
        <ConsultationHubNavigationRow
          items={navigationItems}
          activeItemID={activeNavItem}
          onItemClick={handleNavItemClick}
          colors={colors}
          endAdornment={
            activeTab === 'upcoming' || activeTab === 'past' ? (
              <ConsultationTypeFilterDropdown
                value={consultationTypeFilter}
                onChange={setConsultationTypeFilter}
              />
            ) : null
          }
        />
      )}

      {activeTab === 'overview' && (
        <>
          <Grid item xs={12}>
            {activeCancelUrl ? (
              <CancelConsultation
                cancelUrl={activeCancelUrl}
                onBack={handleCancelBack}
              />
            ) : (
              <OnboardingSection
                consultation={consultation}
                colors={colors}
                onCancelRequested={(url) => setActiveCancelUrl(url)}
              />
            )}
          </Grid>
          {!activeCancelUrl && (
            <Grid item xs={12}>
              <Box mt={theme.spacing(10)} mb={10} sx={{width: '100%'}}>
                {overview}
              </Box>
            </Grid>
          )}
        </>
      )}

      {activeTab === 'upcoming' && (
        <AppointmentList
          items={filteredUpcoming}
          colors={colors}
          status='upcoming'
          loading={appointmentsLoading}
          consultationSlug={hub.onboardingConsultationSlug}
          onCancelBack={handleCancelBack}
        />
      )}

      {activeTab === 'past' && (
        <AppointmentList
          items={filteredPast}
          colors={colors}
          status='completed'
          loading={appointmentsLoading}
          consultationSlug={hub.onboardingConsultationSlug}
        />
      )}
    </>
  )
}

export default P3ConsultationViewContent
